import { uiPaper } from "shared/ui/uiSurface"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from "@mui/material"
import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useGetCourierByIdQuery,
  useCreateCourierMutation,
  useUpdateCourierMutation,
  CourierVehicleInfo,
} from "../../apis/appApi"

type Props = {
  open: boolean
  courierId: number | null
  onClose: () => void
}

function sanitizePart(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
}

function parseVehicleInfo(mobileData: string | null | undefined): CourierVehicleInfo {
  if (!mobileData) return { licensePlate: "", vehicleType: "" }
  try {
    const parsed = JSON.parse(mobileData)
    const vi = parsed?.vehicleInfo
    return {
      licensePlate: vi?.licensePlate ?? "",
      vehicleType: vi?.vehicleType ?? "",
    }
  } catch {
    return { licensePlate: "", vehicleType: "" }
  }
}

const VEHICLE_TYPES = ["", "car", "van", "truck", "motorcycle", "bicycle", "walking"]

export const CourierFormDialog: FC<Props> = ({ open, courierId, onClose }) => {
  const { t } = useTranslation()
  const isEditMode = courierId !== null

  const { data: courierData } = useGetCourierByIdQuery(courierId!, { skip: !courierId })
  const [createCourier, { isLoading: isCreating }] = useCreateCourierMutation()
  const [updateCourier, { isLoading: isUpdating }] = useUpdateCourierMutation()

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [email, setEmail] = useState("")
  const [isManager, setIsManager] = useState(false)
  const [licensePlate, setLicensePlate] = useState("")
  const [vehicleType, setVehicleType] = useState("")
  const [error, setError] = useState("")
  const [editUsername, setEditUsername] = useState("")

  const username = isEditMode
    ? editUsername
    : [sanitizePart(firstName), sanitizePart(lastName)].filter(Boolean).join(".")

  useEffect(() => {
    if (open && courierId && courierData?.data) {
      const c = courierData.data
      setFirstName(c.firstName || "")
      setLastName(c.lastName || "")
      setEditUsername(c.username || "")
      setPhoneNumber(c.phoneNumber || "")
      setEmail(c.email || "")
      setIsManager(
        Array.isArray(c.roles) && c.roles.some((r: any) => r.authority === "ROLE_HAULER_MANAGER"),
      )
      // Parse vehicle info from mobileData (F113)
      const vi = parseVehicleInfo(c.mobileData)
      setLicensePlate(vi.licensePlate ?? "")
      setVehicleType(vi.vehicleType ?? "")
      setError("")
    } else if (open && !courierId) {
      setFirstName("")
      setLastName("")
      setEditUsername("")
      setPhoneNumber("")
      setEmail("")
      setIsManager(false)
      setLicensePlate("")
      setVehicleType("")
      setError("")
    }
  }, [open, courierId, courierData])

  const handleClose = useCallback(() => {
    setError("")
    onClose()
  }, [onClose])

  const handleSubmit = useCallback(async () => {
    try {
      setError("")
      if (isEditMode) {
        await updateCourier({
          id: courierId,
          body: {
            firstName,
            lastName,
            phoneNumber,
            email,
            isManager,
            vehicleInfo: {
              licensePlate: licensePlate || null,
              vehicleType: vehicleType || null,
            },
          },
        }).unwrap()
      } else {
        await createCourier({ firstName, lastName, username, phoneNumber, email, isManager }).unwrap()
      }
      handleClose()
    } catch (err: any) {
      const msg = err?.data?.message || err?.data?.error || "An error occurred"
      setError(msg)
    }
  }, [
    isEditMode,
    courierId,
    firstName,
    lastName,
    phoneNumber,
    email,
    isManager,
    licensePlate,
    vehicleType,
    username,
    createCourier,
    updateCourier,
    handleClose,
  ])

  return (
    <Dialog
      slotProps={{ paper: uiPaper("UI-DLV2-126") }}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="courier-form-dialog"
    >
      <DialogTitle>
        {isEditMode ? t("couriers.edit.title") : t("couriers.create.title")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("couriers.create.firstName")}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            fullWidth
            size="small"
            autoFocus
            data-testid="courier-form-firstName-input"
          />
          <TextField
            label={t("couriers.create.lastName")}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            fullWidth
            size="small"
            data-testid="courier-form-lastName-input"
          />
          <TextField
            label={t("couriers.create.username")}
            value={username}
            fullWidth
            size="small"
            disabled
            data-testid="courier-form-username-input"
          />
          <TextField
            label={t("couriers.create.phone")}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            fullWidth
            size="small"
            data-testid="courier-form-phone-input"
          />
          <TextField
            label={t("couriers.create.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            data-testid="courier-form-email-input"
          />

          {/* Vehicle Info (F113) — only shown in edit mode */}
          {isEditMode && (
            <>
              <TextField
                label={t("couriers.create.licensePlate")}
                value={licensePlate}
                onChange={(e) => setLicensePlate(e.target.value)}
                fullWidth
                size="small"
                data-testid="courier-form-licensePlate-input"
              />
              <FormControl fullWidth size="small">
                <InputLabel>{t("couriers.create.vehicleType")}</InputLabel>
                <Select
                  value={vehicleType}
                  onChange={(e) => setVehicleType(e.target.value)}
                  label={t("couriers.create.vehicleType")}
                  data-testid="courier-form-vehicleType-select"
                >
                  {VEHICLE_TYPES.map((vt) => (
                    <MenuItem key={vt || "__none__"} value={vt}>
                      {vt ? t(`couriers.vehicle.${vt}`) : t("couriers.vehicle.none")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={isManager}
                onChange={(e) => setIsManager(e.target.checked)}
                data-testid="courier-form-isManager-switch"
              />
            }
            label={t("couriers.create.isManager")}
          />
          {error && (
            <Alert severity="error" data-testid="courier-form-error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-testid="courier-form-cancel-btn">
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isCreating || isUpdating || !firstName || !lastName || (!isEditMode && !username)}
          data-testid="courier-form-submit-btn"
        >
          {isEditMode ? t("couriers.edit.submit") : t("couriers.create.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
